import type { PeriodType, PrizeRow } from "../../features/leaderboards/leaderboardsSlice";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/** '2026-10-18' → "18 Oct 2026"; '2026-10' → "October 2026". */
export const periodLabel = (periodType: PeriodType, key: string): string => {
  const [y, m, d] = key.split("-").map(Number);
  if (!y || !m) return key;
  if (periodType === "daily" && !d) return `${MONTHS[m - 1]} ${y}`;
  return periodType === "daily" ? `${d} ${MONTHS[m - 1].slice(0, 3)} ${y}` : `${MONTHS[m - 1]} ${y}`;
};
export const periodHeadline = (periodType: PeriodType, key: string): string => periodLabel(periodType, key).toUpperCase();

export const formatNaira = (n: number): string =>
  `₦${Number(n || 0).toLocaleString("en-NG", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

export type DisplayCurrency = "ngn" | "ghs" | "usd";
export const CURRENCIES: DisplayCurrency[] = ["ngn", "ghs", "usd"];
export const CURRENCY_SYMBOL: Record<DisplayCurrency, string> = { ngn: "₦", ghs: "GH₵", usd: "$" };
export const nextCurrency = (c: DisplayCurrency): DisplayCurrency => CURRENCIES[(CURRENCIES.indexOf(c) + 1) % CURRENCIES.length];

export interface Rate {
  name: string;
  rate: number;
}

/** NGN → display currency via the admin rates (NGN per unit); a missing/zero rate falls back to NGN. */
export const convert = (ngn: number, currency: DisplayCurrency, rates?: Rate[]): { value: number; currency: DisplayCurrency } => {
  if (currency === "ngn") return { value: ngn, currency };
  const rate = rates?.find((r) => r?.name === currency)?.rate;
  if (!rate || !Number.isFinite(rate) || rate <= 0) return { value: ngn, currency: "ngn" };
  const value = ngn / rate;
  return Number.isFinite(value) ? { value, currency } : { value: ngn, currency: "ngn" };
};

export const formatMoney = (ngn: number, currency: DisplayCurrency, rates?: Rate[]): string => {
  const { value, currency: c } = convert(ngn, currency, rates);
  const digits = c === "ngn" ? { minimumFractionDigits: 0, maximumFractionDigits: 2 } : { minimumFractionDigits: 2, maximumFractionDigits: 2 };
  return `${CURRENCY_SYMBOL[c]}${Number(value || 0).toLocaleString("en-NG", digits)}`;
};

export const countdown = (settlesAt: string | Date, now: number = Date.now()): string => {
  const ms = new Date(settlesAt).getTime() - now;
  if (!Number.isFinite(ms)) return "";
  if (ms <= 0) return "settling…";
  const totalMinutes = Math.floor(ms / 60_000);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

export const dailyRewardNote = (prizes: PrizeRow[], money: (ngn: number) => string = formatNaira): string => {
  if (!prizes?.length) return "";
  const sorted = [...prizes].sort((a, b) => a.rank - b.rank);
  const groups: { from: number; to: number; amount: number }[] = [];
  for (const p of sorted) {
    const g = groups[groups.length - 1];
    if (g && g.amount === p.amount && g.to === p.rank - 1) g.to = p.rank;
    else groups.push({ from: p.rank, to: p.rank, amount: p.amount });
  }
  return `Daily rewards: ${groups.map((g) => `#${g.from}${g.to !== g.from ? `–${g.to}` : ""} ${money(g.amount)}`).join(" · ")}`;
};

export const movementLabel = (movement: number | null): string =>
  movement == null ? "new" : movement > 0 ? `▲${movement}` : movement < 0 ? `▼${-movement}` : "·";
